import { useEffect, useState } from 'react';
import type React from 'react';
import type { ProjectType, Timeline } from '../data/business';
import type { Lang } from '../types/content';

export type EstimateFormValues = {
  name: string;
  phone: string;
  email: string;
  projectType: ProjectType | '';
  location: string;
  details: string;
  timeline: Timeline | '';
  language: Lang;
};

export type EstimateField = keyof EstimateFormValues;
export type RequiredField = 'name' | 'phone' | 'email' | 'projectType' | 'location';
type Errors = Partial<Record<RequiredField, true>>;
type Status = 'idle' | 'submitting' | 'success' | 'error';

const requiredOrder: RequiredField[] = ['name', 'phone', 'email', 'projectType', 'location'];

export function useEstimateForm(lang: Lang) {
  const initial: EstimateFormValues = {
    name: '',
    phone: '',
    email: '',
    projectType: '',
    location: '',
    details: '',
    timeline: '',
    language: lang,
  };
  const [values, setValues] = useState<EstimateFormValues>(initial);
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<Status>('idle');
  const [languageTouched, setLanguageTouched] = useState(false);

  useEffect(() => {
    if (!languageTouched) setValues((v) => ({ ...v, language: lang }));
  }, [lang, languageTouched]);

  const update = (field: EstimateField, value: string) => {
    if (field === 'language') setLanguageTouched(true);
    setValues((v) => ({ ...v, [field]: value }));
    if (isRequired(field)) setErrors((e) => ({ ...e, [field]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const next = validate(values);
    setErrors(next);
    const firstInvalid = requiredOrder.find((f) => next[f]);
    if (firstInvalid) {
      document.getElementById(`estimate-${firstInvalid}`)?.focus();
      return;
    }
    setStatus('submitting');
    try {
      await submitEstimate(values);
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  const reset = () => {
    setValues({ ...initial, language: lang });
    setErrors({});
    setStatus('idle');
    setLanguageTouched(false);
  };

  return { values, errors, status, update, handleSubmit, reset };
}

function isRequired(field: EstimateField): field is RequiredField {
  return (requiredOrder as string[]).includes(field);
}

function validate(v: EstimateFormValues): Errors {
  const errors: Errors = {};
  if (v.name.trim().length < 2) errors.name = true;
  if (v.phone.replace(/\D/g, '').length < 10) errors.phone = true;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email.trim())) errors.email = true;
  if (!v.projectType) errors.projectType = true;
  if (!v.location) errors.location = true;
  return errors;
}

function submitEstimate(_values: EstimateFormValues): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, 1200));
}
